import { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Settings, LogIn } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { ConversionGrid } from '../components/converter/ConversionGrid';
import { CurrencySelector } from '../components/converter/CurrencySelector';
import { OfflineIndicator } from '../components/ui/OfflineIndicator';
import { InstallPrompt } from '../components/ui/InstallPrompt';
import { Button } from '../components/ui/Button';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useRates } from '../hooks/useRates';
import { useToast } from '../hooks/useToast';
import { DEFAULT_FAVORITES } from '../lib/constants';
import siteContent from '../data/site-content.json';

function refreshPage() {
  window.location.reload();
}

export function App() {
  const { user, supabase } = useSupabaseClient();
  const { isStale, refreshRates, loading, error, lastFetched, isOffline } = useRates();
  const { success, error: showError } = useToast();
  const [favorites, setFavorites] = useLocalStorage<string[]>('currency_favorites', [...DEFAULT_FAVORITES]);
  const [showSettings, setShowSettings] = useState(false);
  const [showStaleWarning, setShowStaleWarning] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFavorites, setOriginalFavorites] = useState<string[]>([]);

  // Load user favorites from Supabase if authenticated
  useEffect(() => {
    if (!user) return;

    const loadUserFavorites = async () => {
      const { data } = await supabase
        .from('user_favorites')
        .select('symbols')
        .single();

      if (data?.symbols) {
        setFavorites(data.symbols);
      }
    };

    loadUserFavorites();
  }, [user, supabase]);

  // Save user favorites to Supabase
  const saveFavoritesToSupabase = async (newFavorites: string[]) => {
    if (!user) return;

    await supabase
      .from('user_favorites')
      .upsert({
        id: user.id,
        symbols: newFavorites
      });
  };

  const handleAddFavorite = async (currency: string) => {
    const newFavorites = [...favorites, currency];
    setFavorites(newFavorites);
    setHasUnsavedChanges(true);
  };

  const handleRemoveFavorite = async (currency: string) => {
    const newFavorites = favorites.filter(fav => fav !== currency);
    setFavorites(newFavorites);
    setHasUnsavedChanges(true);
  };

  const handleReorderFavorites = async (newFavorites: string[]) => {
    setFavorites(newFavorites);
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    try {
      if (user) {
        await saveFavoritesToSupabase(favorites);
        success('Currency preferences saved successfully!');
      }
      setHasUnsavedChanges(false);
      setOriginalFavorites([...favorites]);
    } catch (err) {
      showError('Failed to save preferences. Please try again.');
    }
  };

  const handleRefreshRates = async () => {
    try {
      await refreshRates();
      success('Exchange rates updated successfully!');
      setShowStaleWarning(false);
      refreshPage();
    } catch (err) {
      showError('Failed to refresh rates. Please try again.');
    }
  };
  const handleShowSettings = () => {
    setOriginalFavorites([...favorites]);
    setHasUnsavedChanges(false);
    setShowSettings(true);
  };

  const handleHideSettings = () => {
    if (hasUnsavedChanges) {
      // Revert changes if not saved
      setFavorites(originalFavorites);
    }
    setShowSettings(false);
    setHasUnsavedChanges(false);
  };

  // Check for unsaved changes when favorites change
  useEffect(() => {
    if (showSettings && originalFavorites.length > 0) {
      const hasChanges = JSON.stringify(favorites) !== JSON.stringify(originalFavorites);
      setHasUnsavedChanges(hasChanges);
    }
  }, [favorites, originalFavorites, showSettings]);

  // Auto-save for non-authenticated users (localStorage only)
  useEffect(() => {
    if (!user && hasUnsavedChanges) {
      const timer = setTimeout(() => {
        setHasUnsavedChanges(false);
        setOriginalFavorites([...favorites]);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [favorites, hasUnsavedChanges, user]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <OfflineIndicator />
      <InstallPrompt />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stale Data Warning */}
        {(isStale || isOffline) && showStaleWarning && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-amber-800 dark:text-amber-300 font-medium">
                  {isOffline ? siteContent.app.offlineMode : siteContent.app.staleDataWarning}
                </p>
                <p className="text-amber-600 dark:text-amber-400 text-sm">
                  Last updated: {new Date(lastFetched).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refreshRates}
                  loading={loading}
                  disabled={isOffline}
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                  title="Refresh exchange rates"
                >
                  {isOffline ? 'Offline' : 'Refresh'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleShowSettings}
                  leftIcon={<Settings className="w-4 h-4" />}
                >
                  Settings
                </Button>
              </div>
              <button
                onClick={() => setShowStaleWarning(false)}
                className="text-amber-600 hover:text-amber-700 p-1"
                aria-label="Dismiss warning"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Login Prompt for Guests */}
        {!user && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
            <p className="text-blue-800 dark:text-blue-300">
              {siteContent.app.loginPrompt}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/login'}
              leftIcon={<LogIn className="w-4 h-4" />}
            >
              Sign In
            </Button>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main Converter */}
          <div className="lg:col-span-8">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {siteContent.app.title}
                </h1>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefreshRates}
                  loading={loading}
                  disabled={isOffline}
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                  title="Refresh exchange rates"
                >
                  {isOffline ? 'Offline' : siteContent.app.refreshRates}
                </Button>
              </div>

              <ConversionGrid currencies={favorites} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sticky top-24">
              {showSettings ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleHideSettings}
                      className="text-gray-600 dark:text-gray-400"
                    >
                      ← Back
                    </Button>
                  </div>
                <CurrencySelector
                  favorites={favorites}
                  onAddFavorite={handleAddFavorite}
                  onRemoveFavorite={handleRemoveFavorite}
                  onReorderFavorites={handleReorderFavorites}
                    onSave={user ? handleSaveChanges : undefined}
                    hasUnsavedChanges={hasUnsavedChanges}
                />
                </div>
              ) : (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Quick Stats
                  </h2>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Active Currencies</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {favorites.length}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Last Updated</span>
                      <span className="font-semibold text-gray-900 dark:text-white text-sm">
                        {lastFetched ? new Date(lastFetched).toLocaleTimeString() : 'Never'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Status</span>
                      <span className={`font-semibold text-sm ${
                        isStale 
                          ? 'text-amber-600 dark:text-amber-400' 
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {isStale ? 'Stale' : 'Fresh'}
                      </span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowSettings}
                    className="w-full mt-4"
                    leftIcon={<Settings className="w-4 h-4" />}
                  >
                    Manage Currencies
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
